import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AmbiguityTypes,
  AnalysisDocumentTypes,
  DocumentTypes,
  SummarizationNamespaces,
  ProxyDocumentTypes,
  EvaluationTypeLabelsEnum,
  queryKeys,
} from '@/app/utils/constants';
import { useTranslations } from 'use-intl';
import AnalysisAssessment from '@/app/components/features/evaluations/analysis/NewAssesment/AnalysisAssessment';
import TenderFeedbackCard from '@/app/components/features/bids/Cards/TenderFeedbackCard';
import { Frame } from '@/app/ui/components/frame';
import { H1, H2 } from '@/app/ui/components/heading';
import NewBidCvAnalysisCard from '@/app/components/features/bids/Cards/NewBidCvAnalysisCard';
import NewBidMethodologyCard from '@/app/components/features/bids/Cards/NewBidMethodologyCard';
import {
  MainContainer,
  MainFrames,
  MainHeader,
  MainHeaderBackButton,
  MainNav,
  MainNavButton,
  MainNavItem,
  MainNavLink,
  MainNavList,
} from '@/app/ui/components/main';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { MainScrollArea } from '@/app/ui/components/main';
import {
  AuctionIcon,
  FolderIcon,
  SettingsIcon,
  TimeQuarterPassIcon,
} from '@hugeicons/core-free-icons';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { HugeiconsIcon } from '@hugeicons/react';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { AmbiguitiesFrame } from '@/app/components/features/evaluations/analysis/Ambiguities/AmbiguitiesFrame';
import { SummaryFrame } from '@/app/components/features/evaluations/frames/SummaryFrame';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { useSuspenseQuery, useQuery } from '@tanstack/react-query';
import { getLatestEvaluationVersion } from '@/app/services/versioningService';
import { useAtom, useSetAtom } from 'jotai';
import { versionPanelOpenAtom } from '@/app/components/features/evaluations/VersionHistoryPanel/store';
import { sidechatOpenAtom } from '@/app/components/shared/Sidechat/store';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tGeneral = useTranslations('general');
  const navigate = useNavigate();
  const { bidId, tenderPackageId, projectId } = Route.useParams();
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));

  const { data: documents, isSuccess: documentsLoaded } = useEntityDocuments(
    bidId,
    DocumentTypes.BID,
  );
  const { data: indexingStatus } = useDocumentsIndexingStatus({
    documentType: ProxyDocumentTypes.BID,
    id: bidId,
    enabled: documentsLoaded && documents.length > 0,
  });

  const areDocumentsIndexed =
    documentsLoaded && documents.length > 0 && indexingStatus
      ? documents!.every((doc: any) =>
          indexingStatus.documents.some(
            (statusItem) => statusItem.docId === doc.id && statusItem.indexed === true,
          ),
        )
      : false;

  const { data: latestVersion } = useQuery({
    queryKey: [queryKeys.EVALUATION_LATEST_VERSION, bidId],
    queryFn: async () => (await getLatestEvaluationVersion(bidId)).data,
  });

  const [versionPanelOpen, setVersionPanelOpen] = useAtom(versionPanelOpenAtom);
  const setSidechatOpen = useSetAtom(sidechatOpenAtom);

  useEffect(() => () => setVersionPanelOpen(false), [setVersionPanelOpen]);

  const tenderFeedbackId = bid?.tenderFeedbackId;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId/bids"
          params={{ projectId, tenderPackageId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{bid.bidAdditionalName || bid.companyName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={bidId}
          summaryType={SummarizationNamespaces.BID}
          onGoToFiles={() =>
            navigate({
              to: '/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/files',
              params: { projectId, tenderPackageId, bidId },
            })
          }
        />
        <MainHeader>
          <H1>
            <MainHeaderBackButton
              to="/project/$projectId/tender-packages/$tenderPackageId/bids"
              params={{ projectId, tenderPackageId }}
            />
            {bid.bidAdditionalName || bid.companyName}
          </H1>
        </MainHeader>
        <MainNav>
          <MainNavList>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/files"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <HugeiconsIcon icon={FolderIcon} />
                {t('nav.files')} {!documents?.length ? null : `(${documents.length})`}
              </MainNavLink>
            </MainNavItem>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/settings"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <HugeiconsIcon icon={SettingsIcon} />
                {t('nav.settings')}
              </MainNavLink>
            </MainNavItem>
            {latestVersion && (
              <MainNavItem>
                <MainNavButton
                  data-selected={versionPanelOpen ? '' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    setVersionPanelOpen((prev) => !prev);
                    if (!versionPanelOpen) setSidechatOpen(false);
                  }}
                >
                  <HugeiconsIcon icon={TimeQuarterPassIcon} />
                  {t('nav.versionHistory')} (v{latestVersion.version.versionNumber}{' '}
                  {latestVersion.version.status === 'active' ? tGeneral('latest') : ''})
                </MainNavButton>
              </MainNavItem>
            )}
          </MainNavList>
        </MainNav>
        <MainContainer>
          <H2 className="mb-3">{t('section1Title')}</H2>
          <AnalysisAssessment documentType={AnalysisDocumentTypes.BID} id={bidId} />
          <H2 className="mb-3 mt-5">{t('section2Title')}</H2>
          <Frame stackedPanels className="@4xl:grid-cols-2">
            <NewBidMethodologyCard enableAnalysis={areDocumentsIndexed} />
            <NewBidCvAnalysisCard enableAnalysis={areDocumentsIndexed} />
          </Frame>
          <H2 className="mb-3 mt-5">{t('section4Title')}</H2>
          <MainFrames>
            <SummaryFrame
              evaluationId={bidId}
              summaryType={SummarizationNamespaces.BID}
              to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/summary"
              params={{ projectId, tenderPackageId, bidId }}
            />
            <AmbiguitiesFrame
              ambiguityType={AmbiguityTypes.BID}
              id={bidId}
              to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/ambiguities"
              params={{ projectId, tenderPackageId, bidId }}
            />
          </MainFrames>
          <div className="flex mb-3 justify-between items-center mt-5">
            <H2>{t('section3Title')}</H2>
          </div>
          <TenderFeedbackCard tenderFeedbackId={tenderFeedbackId} />
        </MainContainer>
      </MainScrollArea>
    </>
  );
}
